import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { BerthDataContext } from "../../contexts/berth-data-context";
import { fetchVoyageSchedule } from "../../store/thunks/voyageScheduleThunks";
import TabbedLayout from "../common/tabbedLayout";

import type { AppDispatch, RootState } from "../../store/store";
import type { Schedule } from "../../utils/types/voyageSchedule";

type BerthData = {
  berthName: string;
  berthType: string | null;
};

const ScheduleDetails = () => {
  const { berthId, id } = useParams();
  const { scheduleList } = useSelector(
    (state: RootState) => state.voyageSchedule
  );

  const [berthData, setBerthData] = useState<BerthData>({
    berthName: "",
    berthType: "Berth",
  });

  const dispatch = useDispatch<AppDispatch>();

  const updateBerthData = useCallback((newData: Partial<BerthData>) => {
    setBerthData((prev) => ({ ...prev, ...newData }));
  }, []);

  const schedules: Schedule[] = useMemo(
    () => (id ? scheduleList?.[id] ?? [] : []),
    [scheduleList, id]
  );

  const contextValue = useMemo(
    () => ({
      berthData,
      updateBerthData,
    }),
    [berthData, updateBerthData]
  );

  useEffect(() => {
    if (!schedules.length) dispatch(fetchVoyageSchedule({ id: id! }));
  }, [id, dispatch, schedules.length]);

  useEffect(() => {
    if (berthId) {
      schedules.find((schedule) => {
        if (schedule.id === berthId) {
          setBerthData({
            berthName: schedule?.stops || "",
            berthType: schedule?.type || "Berth",
          });
          return true;
        }
        return false;
      });
    }
  }, [berthId, schedules, id]);

  const scheduleTabs = useMemo(
    () => [
      { label: "General", path: "general" },
      {
        label: "Cargo",
        path: "cargoes",
        disabled: berthData?.berthType === "Anchorage",
      },
      {
        label: "Deliveries",
        path: "deliveries",
      },
      {
        label: "Bunkers Fresh Water",
        path: "bunkers",
      },
      {
        label: "On Board Services",
        path: "onboardservices",
      },
    ],
    [berthData.berthType]
  );

  const getCurrentTabIndex = useCallback(
    (
      pathname: string,
      tabs: {
        label: string;
        path: string;
      }[]
    ) => {
      const pathParts = pathname?.split("/");
      const baseTab = pathParts[5];
      return tabs.findIndex((tab) => tab.path === baseTab);
    },
    []
  );

  const path = location.pathname
    .split("/berthrotations")[0]
    .concat("/berthrotations");

  const basePath = `${path}/${berthId}`;

  return (
    <BerthDataContext.Provider value={contextValue}>
      <TabbedLayout
        title={`Berth: ${berthData?.berthName ?? ""}`}
        tabs={scheduleTabs}
        basePath={basePath}
        backPath={path}
        getCurrentTabIndex={getCurrentTabIndex}
        header
      />
    </BerthDataContext.Provider>
  );
};

export default ScheduleDetails;
